import { useEffect, useRef, useState } from "react";
import { useTheme, type ThemeMode } from "@/context/ThemeContext";

const options: { value: ThemeMode; label: string; icon: string }[] = [
  { value: "auto", label: "Automático", icon: "ri-computer-line" },
  { value: "light", label: "Claro", icon: "ri-sun-line" },
  { value: "dark", label: "Oscuro", icon: "ri-moon-line" },
];

interface ThemeToggleProps {
  light?: boolean;
}

export default function ThemeToggle({ light = false }: ThemeToggleProps) {
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = options.find((option) => option.value === mode) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const triggerClass = light
    ? "border-primary-50/25 bg-primary-50/10 text-primary-50 hover:bg-primary-50/20"
    : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-10 w-10 items-center justify-center rounded-md border transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${triggerClass}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Tema actual: ${current.label}`}
      >
        <i className={`${current.icon} text-lg leading-none`} aria-hidden="true"></i>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5 shadow-lg"
        >
          <p className="px-3 pb-1 pt-1.5 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            Apariencia
          </p>
          {options.map((option) => {
            const active = option.value === mode;
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setMode(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer ${
                  active
                    ? "bg-primary-100 text-primary-800 font-semibold"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className={`${option.icon} text-base leading-none`} aria-hidden="true"></i>
                </span>
                <span className="flex-1 text-left font-label">{option.label}</span>
                {active && <i className="ri-check-line text-base text-primary-600" aria-hidden="true"></i>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}